/**
 * <price-range-slider> — dual-handle price filter for the vertical facet sidebar.
 *
 * Two overlaid `input[type=range]` elements act as the handles. They carry no `name`, so
 * they never end up in FormData; on change they write the selected bounds into the two
 * hidden inputs that hold Shopify's `filter.v.price.gte` / `.lte` params. The range
 * input's own `input` event bubbles to the facet form, which is what Dawn's
 * `facet-filters-form` debounces into a re-render — by then the hidden inputs are current.
 *
 * A handle parked at its end of the range writes an empty value, so an untouched slider
 * does not pin the collection to a price filter.
 */
if (!customElements.get('price-range-slider')) {
  // Block-scoped: the snippet emits its own <script> tag, so a page rendering this twice
  // would redeclare a top-level const.
  // Must match --price-slider-thumb-width in component-price-slider.css.
  const PRICE_SLIDER_THUMB_WIDTH = 6;

  customElements.define(
    'price-range-slider',
    class PriceRangeSlider extends HTMLElement {
      connectedCallback() {
        this.minInput = this.querySelector('[data-price-slider-handle="min"]');
        this.maxInput = this.querySelector('[data-price-slider-handle="max"]');
        this.minField = this.querySelector('[data-price-slider-field="min"]');
        this.maxField = this.querySelector('[data-price-slider-field="max"]');
        this.minOutput = this.querySelector('[data-price-slider-output="min"]');
        this.maxOutput = this.querySelector('[data-price-slider-output="max"]');
        this.fill = this.querySelector('[data-price-slider-fill]');

        if (!this.minInput || !this.maxInput) return;

        this.rangeMin = Number(this.dataset.rangeMin || 0);
        this.rangeMax = Number(this.dataset.rangeMax || 0);
        this.currencySymbol = this.dataset.currencySymbol || '';

        // `input` repaints the track and labels while dragging but must not reach the
        // facet form, or every intermediate pixel would queue a collection re-render.
        this.onHandleInput = this.onHandleInput.bind(this);
        this.onHandleChange = this.onHandleChange.bind(this);

        [this.minInput, this.maxInput].forEach((input) => {
          input.addEventListener('input', this.onHandleInput);
          input.addEventListener('change', this.onHandleChange);
        });

        this.render();
      }

      disconnectedCallback() {
        [this.minInput, this.maxInput].forEach((input) => {
          if (!input) return;
          input.removeEventListener('input', this.onHandleInput);
          input.removeEventListener('change', this.onHandleChange);
        });
      }

      onHandleInput(event) {
        event.stopPropagation();
        this.clampAgainstSibling(event.currentTarget);
        this.render();
      }

      onHandleChange(event) {
        event.stopPropagation();
        this.clampAgainstSibling(event.currentTarget);
        this.render();
        this.commit();
      }

      /** Stops the handles crossing over each other. */
      clampAgainstSibling(handle) {
        const min = Number(this.minInput.value);
        const max = Number(this.maxInput.value);
        if (min <= max) return;

        if (handle === this.minInput) {
          this.minInput.value = String(max);
        } else {
          this.maxInput.value = String(min);
        }
      }

      /** Paints the filled portion of the track and updates the two money labels. */
      render() {
        const min = Number(this.minInput.value);
        const max = Number(this.maxInput.value);
        const span = this.rangeMax - this.rangeMin;

        if (this.fill && span > 0) {
          const start = (min - this.rangeMin) / span;
          const end = (max - this.rangeMin) / span;
          // A native thumb travels between its own half-widths, not 0–100%, so the fill
          // is expressed against that same inset. Done in calc() rather than by measuring
          // the element, which reads 0 while the sidebar is display:none below 750px.
          const travel = `(100% - ${PRICE_SLIDER_THUMB_WIDTH}px)`;
          const half = `${PRICE_SLIDER_THUMB_WIDTH / 2}px`;
          this.fill.style.insetInlineStart = `calc(${start} * ${travel} + ${half})`;
          this.fill.style.width = `calc(${Math.max(end - start, 0)} * ${travel})`;
        }

        if (this.minOutput) this.minOutput.textContent = this.formatMoney(min);
        if (this.maxOutput) this.maxOutput.textContent = this.formatMoney(max);

        // The topmost handle wins the pointer. Once both sit at the far right, only the
        // min handle can still be moved, so it has to come forward.
        this.minInput.style.zIndex = min >= this.rangeMax ? '4' : '3';
      }

      /** Writes the hidden fields Shopify filters on, then lets the facet form pick them up. */
      commit() {
        const min = Number(this.minInput.value);
        const max = Number(this.maxInput.value);

        if (this.minField) this.minField.value = min > this.rangeMin ? String(min) : '';
        if (this.maxField) this.maxField.value = max < this.rangeMax ? String(max) : '';

        this.dispatchEvent(new Event('input', { bubbles: true }));
      }

      formatMoney(amount) {
        return `${this.currencySymbol}${Math.round(amount).toLocaleString()}`;
      }
    }
  );
}
